/**
 * @fileoverview Gerador determinístico da massa de dados falsa.
 *
 * Volume (controlado por config opcional):
 *   - 3 subsidiárias
 *   - 5 empresas
 *   - 30 Smart Centers
 *   - 100 agentes
 *   - 1.000 clientes
 *   - 10.000 conversas (distribuídas em 12 meses + concentração nas
 *     últimas 24h pra alimentar "do dia" e os abertos)
 *   - 50.000 mensagens (objetos enxutos — id, convId, role, sentAt)
 *
 * Shape espelha o que o backend retornaria por GET /multi-unidades/*.
 * Quando os endpoints existirem, a UI continua igual — só troca esse
 * arquivo por um fetch ao backend.
 *
 * IMPORTANTE: gerar 50k objetos em JS leva ~150ms. Roda 1x no mount,
 * memoizado via useMemo([], []).
 */

import { createRng, rngInt, rngPick, rngWeighted, rngFloat } from "./rng";

const FIRST_NAMES = [
  "Ana", "Bruno", "Carla", "Daniel", "Eduarda", "Felipe", "Gabriela", "Hugo",
  "Isabela", "João", "Karina", "Lucas", "Mariana", "Nícolas", "Olívia", "Paulo",
  "Quésia", "Rafael", "Sofia", "Tiago", "Ursula", "Vinícius", "Wesley", "Yara",
  "Zélia", "Beatriz", "Caio", "Débora", "Elias", "Fernanda", "Gustavo", "Helena",
  "Ítalo", "Júlia", "Lara", "Marcelo", "Nathália", "Otávio", "Patrícia", "Renato",
  "Tatiana", "Vitor",
];

const LAST_NAMES = [
  "Silva", "Santos", "Oliveira", "Souza", "Pereira", "Almeida", "Costa", "Carvalho",
  "Ferreira", "Rodrigues", "Lima", "Gomes", "Ribeiro", "Martins", "Araújo", "Mendes",
  "Rocha", "Cardoso", "Cavalcante", "Fonseca", "Vieira", "Andrade", "Barbosa",
  "Nascimento", "Moreira", "Castro", "Pinto", "Teixeira",
];

const CITIES = [
  "BARRA - RJ", "BELO HORIZONTE - MG", "BRASÍLIA - DF", "CAMPINAS - SP",
  "CURITIBA - PR", "FORTALEZA - CE", "GOIÂNIA - GO", "MANAUS - AM",
  "SALVADOR - BA", "SÃO PAULO - SP", "PORTO ALEGRE - RS", "RECIFE - PE",
  "FLORIANÓPOLIS - SC", "VITÓRIA - ES", "NATAL - RN", "JOÃO PESSOA - PB",
  "MACEIÓ - AL", "CUIABÁ - MT", "CAMPO GRANDE - MS", "TERESINA - PI",
  "BELÉM - PA", "SÃO LUÍS - MA", "PALMAS - TO", "PORTO VELHO - RO",
  "RIO BRANCO - AC", "BOA VISTA - RR", "MACAPÁ - AP", "ARACAJU - SE",
  "PINHEIROS - SP", "TATUAPÉ - SP",
];

const MESSAGE_SAMPLES = [
  "Boa tarde! Estou com problema no meu aparelho.",
  "Vocês podem me ajudar com a garantia?",
  "Ainda não recebi o pedido, podem verificar?",
  "Gostaria de saber mais sobre o plano.",
  "Tudo certo, obrigado pelo atendimento!",
  "Estou aguardando o orçamento desde ontem.",
  "Meu pagamento não foi aprovado, podem verificar?",
  "Vou aguardar o retorno, obrigado.",
  "Quando o técnico passa aqui em casa?",
  "A peça que recebi está com defeito.",
  "Quero cancelar meu pedido.",
  "Pode me enviar a segunda via do boleto?",
  "Funcionou perfeitamente, agradeço a paciência!",
  "Vou conversar com o gerente e retorno.",
  "Ok, fico no aguardo.",
  "Está demorando muito para responder.",
  "Quero falar com um supervisor.",
  "Ótimo atendimento, parabéns à equipe.",
];

const AGENT_REPLIES = [
  "Olá! Tudo bem? Já estou verificando aqui.",
  "Bom dia, vou consultar nossos sistemas e já retorno.",
  "Posso ajudar! Pode me passar o número do seu pedido?",
  "Entendi, vou abrir um chamado pra você.",
  "Aguarde um momento por favor, estou verificando.",
  "Já realizamos a tratativa, vou te enviar a confirmação.",
  "Por favor, me confirma seu CPF para localizar o atendimento.",
  "Acabei de ver aqui, vou resolver agora.",
  "Pode contar conosco. Mais alguma coisa?",
  "Obrigada pelo contato, qualquer coisa estamos à disposição!",
];

const STATUS_BY_FLOW = {
  // status final de uma conversa em aberto, dado o fluxo
  ONLY_CLIENT: "no_reply",       // só cliente mandou, agente nunca respondeu
  AGENT_LAST: "waiting_client",  // agente respondeu por último
  CLIENT_LAST: "waiting_agent",  // cliente respondeu por último (esperando agente)
};

// ── Subsidiárias / Empresas / Smart Centers ───────────────────────────
/**
 * Quando `realCompanies` é passado (lista vinda do backend /companies/list),
 * cada company real vira UM Smart Center na tela — mantém consistência
 * entre a listagem de "Empresas" do admin e a tabela Multi unidades.
 *
 * As subsidiárias são derivadas do `groupName` da company (companies sem
 * groupName ganham uma subsidiária "Independentes").
 *
 * Quando `realCompanies` está vazio ou ausente, cai num conjunto de
 * cidades sintético (modo standalone — útil pra storybook/preview).
 */
const generateOrgs = (rng, realCompanies) => {
  if (realCompanies && realCompanies.length > 0) {
    // Deriva subsidiárias por groupName (companies sem grupo viram
    // "Independentes")
    const groupSet = new Map();
    for (const co of realCompanies) {
      const g = (co.groupName || "").trim();
      const key = g || "independentes";
      if (!groupSet.has(key)) {
        groupSet.set(key, {
          id: `sub-${key}`,
          value: key,
          name: g ? g : "Independentes",
        });
      }
    }
    const subsidiaries = Array.from(groupSet.values());

    const companies = realCompanies.map((co) => {
      const key = (co.groupName || "").trim() || "independentes";
      return {
        id: `co-${co.id}`,
        backendId: co.id,
        name: co.name,
        subsidiaryId: `sub-${key}`,
      };
    });

    // Cada company real = 1 Smart Center com o MESMO nome.
    // ASC Code gerado deterministicamente a partir do id real
    // (mesma company → mesmo ascCode entre reloads).
    const smartCenters = companies.map((co) => {
      const seed = co.backendId * 7919; // primo
      const ascSuffix = ((seed % 9000) + 1000).toString();
      return {
        id: `sc-co-${co.backendId}`,
        name: co.name,
        ascCode: `590${ascSuffix}`,
        companyId: co.id,
        backendCompanyId: co.backendId,
        subsidiaryId: co.subsidiaryId,
      };
    });

    return { subsidiaries, companies, smartCenters };
  }

  // ── Fallback (modo standalone, sem companies do backend) ──────────
  const subsidiaries = [
    { id: "sub-1", value: "samsung-br", name: "Samsung BR" },
    { id: "sub-2", value: "samsung-corp", name: "Samsung Corporativo" },
    { id: "sub-3", value: "samsung-vip", name: "Samsung VIP" },
  ];
  const companies = [
    { id: "co-1", name: "Samsung Eletrônicos LTDA", subsidiaryId: "sub-1" },
    { id: "co-2", name: "Samsung Comercializadora", subsidiaryId: "sub-1" },
    { id: "co-3", name: "Samsung Suporte Premium", subsidiaryId: "sub-2" },
    { id: "co-4", name: "Samsung Corporate Care", subsidiaryId: "sub-2" },
    { id: "co-5", name: "Samsung VIP Atendimento", subsidiaryId: "sub-3" },
  ];
  const smartCenters = [];
  for (let i = 0; i < 30; i++) {
    const co = companies[i % companies.length];
    const city = CITIES[i % CITIES.length];
    smartCenters.push({
      id: `sc-${String(i + 1).padStart(2, "0")}`,
      name: city,
      ascCode: String(5900000 + rngInt(rng, 100, 9999)).padStart(7, "0"),
      companyId: co.id,
      subsidiaryId: co.subsidiaryId,
    });
  }
  return { subsidiaries, companies, smartCenters };
};

// ── Agentes ────────────────────────────────────────────────────────────
const generateAgents = (rng, smartCenters) => {
  const agents = [];
  // ~3-4 agentes por SC (30 SCs → ~100 agentes)
  for (let i = 0; i < 100; i++) {
    const sc = smartCenters[i % smartCenters.length];
    agents.push({
      id: `agent-${String(i + 1).padStart(3, "0")}`,
      name: `${rngPick(rng, FIRST_NAMES)} ${rngPick(rng, LAST_NAMES)}`,
      smartCenterId: sc.id,
    });
  }
  return agents;
};

// ── Clientes ───────────────────────────────────────────────────────────
const generateCustomers = (rng) => {
  const customers = [];
  for (let i = 0; i < 1000; i++) {
    customers.push({
      id: `cust-${String(i + 1).padStart(4, "0")}`,
      name: `${rngPick(rng, FIRST_NAMES)} ${rngPick(rng, LAST_NAMES)}`,
      phone: `+55 ${rngInt(rng, 11, 99)} 9${rngInt(rng, 1000, 9999)}-${rngInt(rng, 1000, 9999)}`,
    });
  }
  return customers;
};

// ── Conversas + mensagens ──────────────────────────────────────────────
/**
 * Gera 10.000 conversas distribuídas nos últimos 12 meses, com pesos
 * realistas: mais conversas nos últimos 30 dias (60%), bastante hoje
 * (15% do total nas últimas 24h), e o resto espalhado.
 *
 * Cada conversa tem 1-8 mensagens (médio 5) → ~50.000 mensagens.
 *
 * Mensagens são objetos enxutos: { id, conversationId, role, sentAt }.
 * Body é gerado on-demand pra preview de casos (não pesa o dataset).
 */
const generateConversationsAndMessages = (rng, smartCenters, agents, customers, now) => {
  const TARGET_CONVS = 10000;
  const TARGET_MSGS = 50000;

  // Constrói pools auxiliares
  const agentsByScId = new Map();
  for (const a of agents) {
    if (!agentsByScId.has(a.smartCenterId)) agentsByScId.set(a.smartCenterId, []);
    agentsByScId.get(a.smartCenterId).push(a);
  }

  // Buckets temporais (peso = % das conversas):
  //   - últimas 24h: 15%
  //   - últimos 7d (excl 24h): 20%
  //   - últimos 30d (excl 7d): 25%
  //   - últimos 90d (excl 30d): 20%
  //   - últimos 365d (excl 90d): 20%
  const MS_DAY = 24 * 60 * 60 * 1000;
  const buckets = [
    { weight: 0.15, fromAgo: 0, toAgo: 1 },
    { weight: 0.20, fromAgo: 1, toAgo: 7 },
    { weight: 0.25, fromAgo: 7, toAgo: 30 },
    { weight: 0.20, fromAgo: 30, toAgo: 90 },
    { weight: 0.20, fromAgo: 90, toAgo: 365 },
  ];

  const conversations = new Array(TARGET_CONVS);
  // Pré-aloca mensagens — média de 5 por conv mas variável.
  const messages = [];
  messages.length = 0; // explicit init for V8

  let msgIdSeq = 1;
  const remainingMsgBudget = { value: TARGET_MSGS };

  for (let i = 0; i < TARGET_CONVS; i++) {
    // Bucket de tempo
    const bucket = rngWeighted(rng, buckets.map((b) => ({ value: b, weight: b.weight })));
    const ageDays = rngFloat(rng, bucket.fromAgo, bucket.toAgo);
    const startedAt = new Date(now.getTime() - ageDays * MS_DAY);

    // Smart Center e cliente aleatórios
    const sc = rngPick(rng, smartCenters);
    const scAgents = agentsByScId.get(sc.id) || [];
    const agent = scAgents.length > 0 ? rngPick(rng, scAgents) : agents[0];
    const customer = rngPick(rng, customers);

    // Quantas mensagens nesta conversa (1-12, média ~5)
    const convsRemaining = TARGET_CONVS - i;
    const avgRemaining = remainingMsgBudget.value / convsRemaining;
    const minM = Math.max(1, Math.floor(avgRemaining - 3));
    const maxM = Math.min(12, Math.ceil(avgRemaining + 3));
    const msgCount = rngInt(rng, minM, maxM);
    remainingMsgBudget.value -= msgCount;

    // Sequência de mensagens. Convenção: primeira mensagem SEMPRE é do
    // cliente (cliente inicia o contato), depois alterna 70% do tempo,
    // mas pode ter o mesmo lado repetindo (ex: agente manda 2 seguidas).
    const msgs = [];
    let role = "client";
    let cursor = startedAt.getTime();
    for (let j = 0; j < msgCount; j++) {
      const gapSec = rngWeighted(rng, [
        { value: rngInt(rng, 10, 60), weight: 4 },
        { value: rngInt(rng, 60, 600), weight: 5 },
        { value: rngInt(rng, 600, 3600), weight: 3 },
        { value: rngInt(rng, 3600, 14400), weight: 2 },
        { value: rngInt(rng, 14400, 86400), weight: 1 },
      ]);
      cursor += gapSec * 1000;
      msgs.push({
        id: msgIdSeq++,
        convId: `conv-${i + 1}`,
        role,
        sentAt: cursor,
      });
      // Próximo turno
      role = rng() < 0.7 ? (role === "client" ? "agent" : "client") : role;
    }

    // Status da conversa (aberta vs fechada)
    // - 65% das antigas (>30d) estão fechadas
    // - 30% das recentes (7-30d) estão fechadas
    // - 10% das últimas 7d estão fechadas
    const closedProb = ageDays > 30 ? 0.65 : ageDays > 7 ? 0.30 : 0.10;
    const isClosed = rng() < closedProb;

    let status;
    let endedAt = null;
    if (isClosed) {
      status = "closed";
      const lastMsg = msgs[msgs.length - 1];
      // Encerra alguns segundos depois da última msg
      endedAt = lastMsg.sentAt + rngInt(rng, 30, 1800) * 1000;
    } else {
      // Aberta: determina sub-status pela última msg
      const lastMsg = msgs[msgs.length - 1];
      const onlyClient = msgs.every((m) => m.role === "client");
      if (onlyClient) status = "no_reply";
      else if (lastMsg.role === "agent") status = "waiting_client";
      else status = "waiting_agent";
    }

    conversations[i] = {
      id: `conv-${i + 1}`,
      smartCenterId: sc.id,
      companyId: sc.companyId,
      subsidiaryId: sc.subsidiaryId,
      agentId: agent.id,
      customerId: customer.id,
      startedAt: startedAt.getTime(),
      endedAt,
      status,
      messageCount: msgs.length,
      // Boundaries computados aqui pra acelerar metrics depois
      firstClientAt: msgs.find((m) => m.role === "client")?.sentAt ?? startedAt.getTime(),
      firstAgentReplyAt: msgs.find((m) => m.role === "agent")?.sentAt ?? null,
      // 2ª msg do cliente (se houver) — usado pra calcular "1ª resposta cliente"
      secondClientAt: (() => {
        let count = 0;
        for (const m of msgs) {
          if (m.role === "client") {
            count++;
            if (count === 2) return m.sentAt;
          }
        }
        return null;
      })(),
      lastMsgAt: msgs.length ? msgs[msgs.length - 1].sentAt : startedAt.getTime(),
      lastMsgRole: msgs.length ? msgs[msgs.length - 1].role : null,
    };

    // Anexa mensagens ao pool global (mantém slim — sem body)
    for (const m of msgs) messages.push(m);
  }

  return { conversations, messages };
};

// ── SMS Alerts (configuração estática + counters dinâmicos derivados) ──
const generateSmsAlertsBase = (rng, smartCenters, agents) => {
  // Configuração de níveis é fixa
  const alertLevels = [
    { level: 1, minutes: 5, label: "1º (alerta)" },
    { level: 2, minutes: 10, label: "2º" },
    { level: 3, minutes: 15, label: "3º" },
    { level: 4, minutes: 20, label: "4º" },
    { level: 5, minutes: 30, label: "5º (> 30 min)" },
  ];

  // 5 partners (Smart Centers escolhidos pelo seed) + usuários cadastrados
  // Atribui usuários reais (subset dos agents) a cada partner com um nível.
  const partners = [];
  // Pega 5 SCs distribuídos
  const pickedScs = smartCenters
    .slice(0, 5)
    .map((sc, idx) => ({
      smartCenter: sc.name,
      ascCode: idx === 0 ? sc.ascCode : null,
      category: idx === 0 ? null : (idx === 1 ? "MSC" : "CSP"),
      scId: sc.id,
    }));

  for (const p of pickedScs) {
    const scAgents = agents.filter((a) => a.smartCenterId === p.scId);
    // 6-11 usuários por partner
    const userCount = rngInt(rng, 6, 11);
    const users = [];
    for (let i = 0; i < userCount; i++) {
      const baseAgent = scAgents[i % Math.max(1, scAgents.length)] || agents[i];
      users.push({
        id: `${p.scId}-u${i + 1}`,
        name: baseAgent ? baseAgent.name : `Agente ${i + 1}`,
        whatsapp: `+55 ${rngInt(rng, 11, 99)} 9${rngInt(rng, 1000, 9999)}-${rngInt(rng, 1000, 9999)}`,
        alertLevel: rngWeighted(rng, [
          { value: 1, weight: 3 },
          { value: 2, weight: 3 },
          { value: 3, weight: 2 },
          { value: 4, weight: 2 },
          { value: 5, weight: 1 },
        ]),
      });
    }
    partners.push({ ...p, users });
  }

  return { alertLevels, partners };
};

// ── Public: generate everything ───────────────────────────────────────
/**
 * @param {{seed?: number, now?: Date, realCompanies?: Array<{id, name, groupName?}>}} config
 */
export const generateDataset = (config = {}) => {
  const seed = config.seed ?? 0xCafeBabe;
  const now = config.now ?? new Date();
  const rng = createRng(seed);

  const { subsidiaries, companies, smartCenters } = generateOrgs(rng, config.realCompanies);
  const agents = generateAgents(rng, smartCenters);
  const customers = generateCustomers(rng);
  const { conversations, messages } = generateConversationsAndMessages(
    rng, smartCenters, agents, customers, now
  );
  const smsBase = generateSmsAlertsBase(rng, smartCenters, agents);

  return {
    now,
    subsidiaries,
    companies,
    smartCenters,
    agents,
    customers,
    conversations,
    messages,
    smsBase,
  };
};

// ── Helper: preview de uma mensagem (usado nos casos abertos da unidade)
const previewSeqIndex = (rng) => Math.floor(rng() * MESSAGE_SAMPLES.length);

export const generateMessagePreview = (conv, agentName) => {
  // PRNG efêmero seedado por convId pra ser estável entre renders
  const seedFromId = (() => {
    let s = 0;
    for (let i = 0; i < conv.id.length; i++) s = (s * 31 + conv.id.charCodeAt(i)) >>> 0;
    return s || 1;
  })();
  const rng = createRng(seedFromId);
  // Se a última msg é do cliente, preview vem dele
  const lastRole = conv.lastMsgRole || "client";
  const pool = lastRole === "client" ? MESSAGE_SAMPLES : AGENT_REPLIES;
  const sentence = pool[previewSeqIndex(rng)];
  const prefix = lastRole === "client" ? "Cliente: " : `${agentName}: `;
  return `${prefix}${sentence}`;
};
